import { code, codes } from "currency-codes";
import Dinero, { type Currency } from "dinero.js";

export type { Currency };

export const CURRENCIES: ReadonlyArray<Currency> = Object.freeze(
  codes() as unknown as ReadonlyArray<Currency>
);
interface SerializedAmount {
  amount: number;
  currency: Currency;
  precision: number;
}

export const AMOUNT_SAFE_MAX = 1_000_000_000_000_000;
const defaultLocale = "de-de";

/**
 * Amount class that wraps dinero.js functionality
 * Used for handling monetary values in the application
 */
export class Amount {
  private dinero: Dinero.Dinero;

  private constructor(dinero: Dinero.Dinero) {
    this.dinero = dinero;
  }

  static fromAmountAndCurrency(amount: number, currency: string): Amount {
    if (Math.floor(amount) !== amount) {
      throw new TypeError(`Amount has to be an integer, got ${amount}`);
    }

    if (Math.abs(amount) > AMOUNT_SAFE_MAX) {
      throw new RangeError(
        `Amount out of range: ${amount}. Must be between -${AMOUNT_SAFE_MAX} and ${AMOUNT_SAFE_MAX}`
      );
    }
    const precision = code(currency)?.digits;
    if (precision === undefined) {
      throw new TypeError(`Unknown currency ${currency}`);
    }
    return new Amount(
      Dinero({
        amount,
        currency: currency as unknown as Currency,
        precision,
      }).setLocale(defaultLocale)
    );
  }

  static fromDecimal(unit: number, currency: Currency): Amount {
    const precision = code(currency)?.digits;
    if (precision === undefined) {
      throw new TypeError(`Unknown currency ${currency}`);
    }
    const factor = 10 ** precision;
    const roundedAmount = Math.round(unit * factor);

    if (roundedAmount / factor !== unit) {
      throw new TypeError(
        `Unit ${unit} does not match precision ${precision} of currency ${currency}`
      );
    }

    return Amount.fromAmountAndCurrency(roundedAmount, currency);
  }

  static isAmount(value: unknown): value is Amount {
    return value instanceof Amount;
  }

  static serialise(amount: Amount) {
    return amount.toJSON();
  }

  static deserialise(obj: SerializedAmount): Amount {
    return new Amount(Dinero(obj).setLocale(defaultLocale));
  }

  getDecimalAmount() {
    return this.dinero.toUnit();
  }

  getAmount() {
    return this.dinero.getAmount();
  }

  getCurrency() {
    return this.dinero.getCurrency();
  }

  format(format?: string) {
    return this.dinero.toFormat(format);
  }

  toLocaleString(locale: string) {
    return Dinero(this.toJSON()).setLocale(locale).toFormat();
  }

  toString() {
    return this.format();
  }

  toJSON() {
    return this.dinero.toJSON();
  }

  /**
   * @throws Error if currencies don't match
   */
  add(amount: Amount): Amount {
    const result = this.dinero.add(amount.dinero);
    return new Amount(result);
  }

  /**
   * @throws Error if currencies don't match
   */
  subtract(amount: Amount): Amount {
    const result = this.dinero.subtract(amount.dinero);
    return new Amount(result);
  }

  equals(amount: Amount): boolean {
    return this.dinero.equalsTo(amount.dinero);
  }
}
